import React, { useEffect, useMemo, useState } from "react";
import {
	Modal,
	Select,
	Table,
	Checkbox,
	Button,
	Space,
	Typography,
	message,
	InputNumber,
} from "antd";
import { useNavigate } from "react-router-dom";
import Image from "../image/Image";
import { FaImages } from "react-icons/fa";
import { COLORS } from "../../layout/theme";
import { AntdTable, FilterContainer, TableContainer } from "./styles";

const { Text } = Typography;

export default function SuggestedProductsModal({
	open,
	onClose,
	disabled,
	fetchMyPointsOfSale,
	fetchSuggestedByPos,
	addBulkToCart,
	userId,
}) {
	const navigate = useNavigate();

	const [loadingPos, setLoadingPos] = useState(false);
	const [loadingSuggested, setLoadingSuggested] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const [pointsOfSale, setPointsOfSale] = useState([]);
	const [selectedPosId, setSelectedPosId] = useState(null);

	const [suggested, setSuggested] = useState([]);

	const [selectedMap, setSelectedMap] = useState({});
	const [qtyMap, setQtyMap] = useState({});

	const selectedIds = useMemo(
		() => Object.keys(selectedMap).filter((k) => !!selectedMap[k]),
		[selectedMap],
	);

	const allChecked =
		suggested.length > 0 && selectedIds.length === suggested.length;
	const someChecked =
		selectedIds.length > 0 && selectedIds.length < suggested.length;

	useEffect(() => {
		if (!open) return;

		const loadPos = async () => {
			try {
				setLoadingPos(true);
				const pos = await fetchMyPointsOfSale(userId);
				const list = pos?.pointsOfSale || [];

				setPointsOfSale(list);

				if (list.length === 1) {
					setSelectedPosId(list[0].id);
				} else {
					setSelectedPosId(null);
				}
			} catch (e) {
				message.error("No se pudieron cargar los puntos de venta.");
			} finally {
				setLoadingPos(false);
			}
		};

		loadPos();
	}, [open, userId, fetchMyPointsOfSale]);

	useEffect(() => {
		if (!open || !selectedPosId) {
			setSuggested([]);
			setSelectedMap({});
			setQtyMap({});
			return;
		}

		const loadSuggested = async () => {
			try {
				setLoadingSuggested(true);

				const rows = await fetchSuggestedByPos(selectedPosId);
				const list = rows?.suggesteds || [];

				setSuggested(list);

				const map = {};
				const qmap = {};

				list.forEach((r) => {
					const pid = String(r.productId);

					map[pid] = true;

					const initial = Number.isFinite(Number(r.suggestedQty))
						? Number(r.suggestedQty)
						: 1;
					qmap[pid] = initial;
				});

				setSelectedMap(map);
				setQtyMap(qmap);
			} catch (e) {
				message.error("No se pudieron cargar los sugeridos.");
			} finally {
				setLoadingSuggested(false);
			}
		};

		loadSuggested();
	}, [open, selectedPosId, fetchSuggestedByPos]);

	const columns = [
		{
			title: (
				<Checkbox
					indeterminate={someChecked}
					checked={allChecked}
					onChange={(e) => {
						const checked = e.target.checked;
						const next = {};
						suggested.forEach((r) => {
							const pid = String(r.productId);
							next[pid] = checked;
						});
						setSelectedMap(next);
					}}
				/>
			),
			dataIndex: "productId",
			width: 50,
			render: (productId) => {
				const pid = String(productId);
				return (
					<Checkbox
						checked={!!selectedMap[pid]}
						onChange={(e) => {
							const checked = e.target.checked;
							setSelectedMap((prev) => ({ ...prev, [pid]: checked }));
						}}
					/>
				);
			},
		},
		{
			title: () => (
				<div style={{ fontSize: "18px" }}>
					<FaImages style={{ color: COLORS.White }} />
				</div>
			),
			dataIndex: "photoUrl",
			key: "photoUrl",
			width: 100,
			align: "center",
			render: (value, record) => (
				<div key={`${value}`}>
					<Image width={40} height={40} src={value} alt={record.title} />
				</div>
			),
		},
		{
			title: <div style={{ color: COLORS.White }}>Producto</div>,
			dataIndex: "title",
			render: (v) => (
				<div style={{ display: "flex", flexDirection: "column" }}>
					<Text strong>{v}</Text>
				</div>
			),
		},
		{
			title: <div style={{ color: COLORS.White }}>Ean13</div>,
			dataIndex: "ean13",
			render: (v) => (
				<div style={{ display: "flex", flexDirection: "column" }}>
					<Text>{v}</Text>
				</div>
			),
		},
		{
			title: <div style={{ color: COLORS.White }}>Cant.</div>,
			dataIndex: "productId",
			width: 160,
			render: (productId, row) => {
				const pid = String(productId);
				const isSelected = !!selectedMap[pid];

				const fallbackQty = Number.isFinite(Number(row?.suggestedQty))
					? Number(row.suggestedQty)
					: 1;

				const value = qtyMap[pid] ?? fallbackQty;

				return (
					<InputNumber
						min={1}
						step={1}
						precision={0}
						value={value}
						disabled={!isSelected}
						style={{ width: 120, borderRadius: 15 }}
						onChange={(val) => {
							const next = val == null ? 1 : Number(val);
							setQtyMap((prev) => ({ ...prev, [pid]: next }));

							if (!selectedMap[pid]) {
								setSelectedMap((prev) => ({ ...prev, [pid]: true }));
							}
						}}
					/>
				);
			},
		},
	];

	const handleSubmit = async () => {
		if (!selectedPosId) return message.warning("Seleccioná un punto de venta.");

		const picked = suggested.filter((r) => selectedMap[String(r.productId)]);
		if (picked.length === 0)
			return message.warning("Seleccioná al menos un producto.");

		const invalid = picked.find((r) => {
			const pid = String(r.productId);
			const q = qtyMap[pid];
			return !Number.isFinite(Number(q)) || Number(q) < 1;
		});
		if (invalid) return message.warning("Revisá las cantidades (mínimo 1).");

		const items = picked.map((r) => {
			const pid = String(r.productId);
			return {
				productId: r.productId,
				quantity: Number(qtyMap[pid]),
			};
		});

		try {
			setSubmitting(true);
			await addBulkToCart({ pointOfSaleId: selectedPosId, items });
			message.success("Sugeridos agregados al carro.");
			onClose?.();
			navigate(`/cart?pointOfSaleId=${encodeURIComponent(selectedPosId)}`);
		} catch (e) {
			message.error("No se pudo enviar al carro.");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title="Sugeridos"
			width={900}
			footer={
				<Space>
					<Button onClick={onClose}>Cerrar</Button>
					<Button
						type="primary"
						loading={submitting}
						onClick={handleSubmit}
						disabled={disabled || loadingPos || loadingSuggested}
					>
						Enviar al carro
					</Button>
				</Space>
			}
		>
			<Space direction="vertical" style={{ width: "100%" }} size={12}>
				<FilterContainer>
					<Text strong>Punto de venta:</Text>
					<Select
						style={{ width: 420 }}
						loading={loadingPos}
						value={selectedPosId}
						placeholder="Seleccioná un punto de venta"
						onChange={setSelectedPosId}
						disabled={pointsOfSale.length <= 1}
						options={pointsOfSale.map((p) => ({
							value: p.id,
							label: `${p.name}${p.clientName ? ` — ${p.clientName}` : ""}`,
						}))}
					/>
				</FilterContainer>
				<TableContainer containerBackground={COLORS.White}>
					<AntdTable
						rowKey={(row) => String(row.productId)}
						columns={columns}
						dataSource={suggested}
						loading={loadingSuggested}
						pagination={{ pageSize: 8 }}
					/>
				</TableContainer>
			</Space>
		</Modal>
	);
}
